import React from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import TeamMemberRow from './TeamMemberRow';

interface TeamMember {
  user_id: string;
  email: string;
  role: string;
  status: 'active' | 'pending_approval';
  joined_at: string;
  last_login: string;
}

interface TeamMemberListProps {
  teamMembers: TeamMember[];
  onApprove: (userId: string) => void;
  onRemove: (userId: string, email: string) => void;
  onRoleChange: (userId: string, newRole: string) => void;
  isAdmin: boolean;
}

const TeamMemberList: React.FC<TeamMemberListProps> = ({
  teamMembers,
  onApprove,
  onRemove,
  onRoleChange,
  isAdmin
}) => {
  // Ensure teamMembers is always an array
  const safeTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];
  
  if (!safeTeamMembers || safeTeamMembers.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-foreground">Team Members</h2>
          <p className="text-sm text-muted-foreground mt-1">0 members in your team</p>
        </div>
        
        {/* Enhanced Empty State */}
        <div className="text-center py-16">
          <div className="mx-auto h-24 w-24 bg-muted rounded-full flex items-center justify-center">
            <UsersIcon className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-foreground">No team members yet</h3>
          <p className="mt-2 text-muted-foreground max-w-md mx-auto">
            Get started by inviting your first team member. They'll receive an email invitation to join your workspace.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-lg font-medium text-foreground">Team Members</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {safeTeamMembers.length} member{safeTeamMembers.length !== 1 ? 's' : ''} in your team
        </p>
      </div>

      <div className="divide-y divide-border">
        {safeTeamMembers.map((member) => (
          <TeamMemberRow
            key={member.user_id}
            member={member}
            onApprove={onApprove}
            onRemove={onRemove}
            onRoleChange={onRoleChange}
            isAdmin={isAdmin}
          />
        ))}
      </div>
    </div>
  );
};

export default TeamMemberList;

